// src/components/RoomTable.jsx
import React from 'react';
import { typeByKey, UNCLASSIFIED } from '../utils/spaceTypes.js';
import { polyArea } from '../utils/planGeometry.js';

/**
 * The schedule of tagged spaces, and the numbered badge that ties each row to
 * its room on the drawing.
 *
 * Both live here so the two can never disagree: the number in the table and the
 * number on the plan come from one ordering, applied once. Numbering is reading
 * order (see readingOrder in planGeometry) — the architect finds row 7 by
 * scanning the plan the way they would read it, not by hunting a colour.
 *
 * Dimensions are the room's bounding box, which for a rectangle IS the room and
 * for an edited polygon is its extent. Area is the true polygon area, so an
 * L-shaped room reports less than its box implies — never the other way round.
 */

const T = {
  surface: 'var(--surface-2,#1a2234)',
  border: 'var(--border,#3a4456)',
  text: 'var(--text,#e6ebf5)',
  dim: 'var(--text-dim,#9aa7c0)',
  accent: 'var(--accent)',
};

const M_TO_FT = 3.280839895;
const M2_TO_FT2 = 10.76391042;

/**
 * Metres as feet and inches, the way a plan is annotated: 4.20 m -> 13' 9".
 *
 * Inches are rounded, so 11.6" has to carry into the next foot — without that
 * a room reads as 13' 12", which looks like a bug to the one audience that
 * would notice.
 */
function feetInches(m) {
  const totalIn = Math.round(m * M_TO_FT * 12);
  const ft = Math.floor(totalIn / 12);
  const inch = totalIn % 12;
  return `${ft}′ ${inch}″`;
}

/**
 * A typed length back to metres. Accepts what an architect actually types:
 * "4.2", "4.2m", "4200mm", "13'9", "13'-9\"", "13.8ft".
 * Returns null on anything it cannot read, so a half-typed value never resizes
 * a room mid-keystroke.
 */
export function parseLengthM(raw) {
  const t = String(raw || '').trim().toLowerCase().replace(/[’′]/g, "'").replace(/[”″]/g, '"');
  if (!t) return null;
  const fi = t.match(/^(\d+(?:\.\d+)?)\s*'\s*-?\s*(\d+(?:\.\d+)?)?\s*"?$/);
  if (fi) {
    const inch = fi[2] ? parseFloat(fi[2]) : 0;
    if (inch >= 12) return null;
    return (parseFloat(fi[1]) + inch / 12) * 0.3048;
  }
  const num = t.match(/^(\d+(?:\.\d+)?)\s*(mm|cm|m|ft|feet|')?$/);
  if (!num) return null;
  const v = parseFloat(num[1]);
  switch (num[2]) {
    case 'mm': return v / 1000;
    case 'cm': return v / 100;
    case 'ft': case 'feet': case "'": return v * 0.3048;
    default: return v;
  }
}

/** Numbered badge drawn at a room's centre, in SVG user units. */
export function RoomBadge({ room, w, dimmed }) {
  const t = typeByKey(room.type);
  const col = t ? t.color : UNCLASSIFIED;
  const r = Math.max(6, w / 75);
  const [cx, cy] = room.centroid;
  return (
    <g pointerEvents="none" opacity={dimmed ? 0.55 : 1}>
      <circle cx={cx} cy={cy} r={r} fill={col} stroke="#0d1420" strokeWidth={r * 0.14} />
      <text
        x={cx} y={cy}
        textAnchor="middle" dominantBaseline="central"
        fontSize={r * 1.25} fontWeight="700" fill="#0d1420"
        style={{ fontFamily: 'system-ui, sans-serif' }}
      >
        {room.n}
      </text>
    </g>
  );
}

/**
 * Cell renderers live at module scope on purpose.
 *
 * A component declared inside RoomTable is a NEW component type on every render,
 * so React unmounts and remounts it — which drops focus from an <input> on every
 * keystroke. Harmless for read-only cells, fatal for editable ones.
 */

/** Length, read-only: metres over feet-inches, or bare pixels with no scale. */
function Len({ px, mpp }) {
  if (!mpp) return <span style={S.primary}>{Math.round(px)}</span>;
  const m = px * mpp;
  return (
    <>
      <div style={S.primary}>{m.toFixed(2)} m</div>
      <div style={S.secondary}>{feetInches(m)}</div>
    </>
  );
}

/** Area, always read-only — it follows from the shape rather than driving it. */
function Area({ px2, mpp }) {
  if (!mpp) return <span style={S.primary}>{Math.round(px2).toLocaleString()}</span>;
  const m2 = px2 * mpp * mpp;
  return (
    <>
      <div style={S.primary}>{m2.toFixed(1)} m²</div>
      <div style={S.secondary}>{Math.round(m2 * M2_TO_FT2).toLocaleString()} ft²</div>
    </>
  );
}

/**
 * Length, editable. Commits on Enter or blur, never mid-keystroke — resizing a
 * room while "4" is on its way to becoming "4.2" would fight the typist and
 * could trip the wall-gap check on a value nobody meant.
 */
function LenEdit({ px, mpp, onCommit }) {
  const shown = mpp ? (px * mpp).toFixed(2) : String(Math.round(px));
  const [draft, setDraft] = React.useState(shown);
  const [focused, setFocused] = React.useState(false);
  React.useEffect(() => { if (!focused) setDraft(shown); }, [shown, focused]);

  const commit = () => {
    setFocused(false);
    const m = mpp ? parseLengthM(draft) : Number(draft);
    if (m && m > 0) onCommit(m); else setDraft(shown);
  };
  const bad = draft !== shown && !(mpp ? parseLengthM(draft) : Number(draft) > 0);

  return (
    <>
      <input
        value={draft}
        onFocus={() => setFocused(true)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') { setDraft(shown); e.currentTarget.blur(); }
        }}
        onClick={(e) => e.stopPropagation()}
        style={{ ...S.input,
                 ...(focused ? S.inputOn : null),
                 ...(bad ? { borderColor: 'var(--err, #d9534f)',
                             boxShadow: '0 0 0 2px color-mix(in srgb, var(--err) 20%, transparent)' }
                         : null) }}
        title={mpp ? "metres, or type 13'-9\" / 4200mm" : 'pixels'}
      />
      {mpp && <div style={S.secondary}>{feetInches(px * mpp)}</div>}
    </>
  );
}

/** Free-text room name. The space type — and so the colour — follows the name. */
function NameEdit({ value, placeholder, onCommit }) {
  const [draft, setDraft] = React.useState(value || '');
  const [focused, setFocused] = React.useState(false);
  React.useEffect(() => { if (!focused) setDraft(value || ''); }, [value, focused]);
  return (
    <input
      value={draft}
      placeholder={placeholder}
      onFocus={() => setFocused(true)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { setFocused(false); onCommit(draft.trim()); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') { setDraft(value || ''); e.currentTarget.blur(); }
      }}
      onClick={(e) => e.stopPropagation()}
      style={{ ...S.input, ...(focused ? S.inputOn : null),
               textAlign: 'left', width: '100%' }}
    />
  );
}

/**
 * Group rows by floor plan.
 *
 * A sheet may hold one plan or several — that is the uploader's choice, not
 * something the tool should dictate — so the schedule sections itself by
 * whatever was found. Numbering runs CONTINUOUSLY across sections rather than
 * restarting per floor: the badges all sit on one drawing, and two rooms both
 * labelled "3" on the same sheet would be unreadable.
 */
function groupByPanel(rooms, panels) {
  if (!panels || panels.length < 2) return [{ key: 'all', title: null, rows: rooms }];
  const out = panels.map((p) => ({
    key: `p${p.index}`,
    title: p.label,
    guessed: p.labelGuessed,
    rows: rooms.filter((r) => r.panel === p.index),
  }));
  const loose = rooms.filter((r) => r.panel == null);
  if (loose.length) {
    out.push({
      key: 'none',
      title: 'Outside every plan',
      note: 'sheet border, gaps between plans, title block — not building',
      rows: loose,
    });
  }
  return out.filter((g) => g.rows.length);
}

export default function RoomTable({
  rooms, panels, mpp, selectedId, onSelect, onNeedScale, onRename, onResize,
  showOffPlan, onToggleOffPlan, onModel, modelShown, editing, onToggleEdit,
  dirty, onSave, onReset, saving, afterHead,
}) {
  const editable = !!(onRename && onResize);
  if (!rooms.length) return null;

  // Regions on no panel are the sheet border, the gaps between plans and the
  // title block — paper, not building. Hidden by default: they are never what a
  // user came to look at, and they dominate the totals when left in.
  const offPlan = (panels || []).length ? rooms.filter((r) => r.panel == null).length : 0;
  const shown = offPlan && !showOffPlan ? rooms.filter((r) => r.panel != null) : rooms;
  const totalArea = shown.reduce((s, r) => s + polyArea(r.pts), 0);

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        Room schedule{' '}
        <span style={S.dim}>
          · {shown.length} tagged
          {mpp
            ? ` · ${(totalArea * mpp * mpp).toFixed(1)} m² / ` +
              `${Math.round(totalArea * mpp * mpp * M2_TO_FT2).toLocaleString()} ft² total`
            : ` · ${rooms.length} regions, no scale yet`}
        </span>
        {!mpp && onNeedScale && (
          <button style={S.cta} onClick={onNeedScale}>Set a scale →</button>
        )}
        {/* Save and Reset stand IN PLACE OF Edit once something has changed.
            Leaving the editor is what used to discard the corrections silently,
            so while there is unsaved work the only ways out are the two that
            say what they will do to it. */}
        {onToggleEdit && !dirty && (
          <button
            style={{ ...S.model, ...(editing ? S.actionOn : null), marginLeft: 'auto' }}
            onClick={onToggleEdit}
            title={editing
              ? 'Editing — click to go back to checking the extraction'
              : 'Edit room names, sizes and shapes'}
          >
            Edit
          </button>
        )}
        {onToggleEdit && dirty && (
          <>
            <button
              style={{ ...S.model, ...S.save, marginLeft: 'auto',
                       ...(saving ? { opacity: 0.6, cursor: 'default' } : null) }}
              onClick={saving ? undefined : onSave}
              title="Store these rooms against the drawing, so they survive leaving the editor"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              style={{ ...S.model, ...S.reset,
                       ...(saving ? { opacity: 0.6, cursor: 'default' } : null) }}
              onClick={saving ? undefined : onReset}
              title="Discard every change and go back to the rooms as they were extracted"
            >
              Reset
            </button>
          </>
        )}
        {onModel && (
          <button
            style={{ ...S.model, marginLeft: onToggleEdit ? 0 : 'auto',
                     ...(modelShown ? S.actionOn : null),
                     ...(mpp ? null : { opacity: 0.45, cursor: 'default' }) }}
            onClick={mpp ? onModel : undefined}
            title={!mpp
              ? 'Set a scale first — without metres there is nothing to extrude'
              : modelShown
              ? 'Model is in the 3D view — click to hide it'
              : 'Extrude these rooms into the 3D view, floor by floor'}
          >
            Model
          </button>
        )}
      </div>
      {/* Whatever the caller wants between the header and the rows — the
          editor puts the placement controls here, under the button that
          builds the thing being placed. */}
      {afterHead}

      <div style={S.scroll}>
        <table style={S.table}>
          {/* Fixed columns. Without them each floor group re-measured its own widths,
              so the number columns shifted between groups and the eye lost the line it
              was reading down. Width and depth get the same width so the two read as a
              pair. */}
          {/* Proportional, not pixel. Fixed widths meant the number columns kept their
              310px whatever the panel did, so narrowing the window ate the Space column
              until the name field collided with Width. Percentages divide whatever width
              there is, and minWidth on the table hands the job to the scroller rather
              than crushing every column below legibility. */}
          <colgroup>
            <col style={{ width: '9%' }} />
            <col style={{ width: '33%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '18%' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={S.th}>#</th>
              <th style={S.th}>Space</th>
              {/* Unit on its own line. "Area m² / ft²" on one nowrap line is wider than the
                  column it sits in, so the header was clipped at the right edge of the
                  panel. Stacked, the widest part is the word itself. */}
              <th style={{ ...S.th, ...S.right }}>
                Width<div style={S.thUnit}>{mpp ? 'm / ft' : 'px'}</div>
              </th>
              <th style={{ ...S.th, ...S.right }}>
                Depth<div style={S.thUnit}>{mpp ? 'm / ft' : 'px'}</div>
              </th>
              <th style={{ ...S.th, ...S.right }}>
                Area<div style={S.thUnit}>{mpp ? 'm² / ft²' : 'px²'}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {groupByPanel(shown, panels).map((g) => (
              <React.Fragment key={g.key}>
                {g.title && (
                  <tr>
                    <td colSpan={5} style={S.group}>
                      {g.title}
                      <span style={S.groupCount}>{g.rows.length} spaces</span>
                      {g.guessed && <span style={S.guess}>name guessed</span>}
                      {g.note && <span style={S.groupNote}>{g.note}</span>}
                    </td>
                  </tr>
                )}
                {g.rows.map((r) => {
              const t = typeByKey(r.type);
              const col = t ? t.color : UNCLASSIFIED;
              const on = r.id === selectedId;
              const a = polyArea(r.pts);
              return (
                <tr
                  key={r.id}
                  className="decon-room-row"
                  onClick={() => onSelect?.(r.id)}
                  style={{ ...S.tr, ...(on ? S.trOn : null), cursor: onSelect ? 'pointer' : 'default' }}
                >
                  <td style={S.td}>
                    <span style={{ ...S.badge, background: col }}>{r.n}</span>
                  </td>
                  <td style={{ ...S.td, color: t ? T.text : T.dim }}>
                    {editable ? (
                      <NameEdit
                        value={r.label || (t ? t.name : '')}
                        placeholder="Unclassified"
                        onCommit={(name) => onRename(r.id, name)}
                      />
                    ) : (
                      t ? t.name : (r.label || 'Unclassified')
                    )}
                  </td>
                  <td style={{ ...S.td, ...S.right }}>
                    {editable
                      ? <LenEdit px={r.box.x1 - r.box.x0} mpp={mpp}
                                 onCommit={(m) => onResize(r.id, 'w', m)} />
                      : <Len px={r.box.x1 - r.box.x0} mpp={mpp} />}
                  </td>
                  <td style={{ ...S.td, ...S.right }}>
                    {editable
                      ? <LenEdit px={r.box.y1 - r.box.y0} mpp={mpp}
                                 onCommit={(m) => onResize(r.id, 'h', m)} />
                      : <Len px={r.box.y1 - r.box.y0} mpp={mpp} />}
                  </td>
                  <td style={{ ...S.td, ...S.right }}><Area px2={a} mpp={mpp} /></td>
                </tr>
              );
            })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {offPlan > 0 && onToggleOffPlan && (
        <div style={S.footRow}>
          <button
            style={{ ...S.cta, ...(showOffPlan ? S.ctaOn : null) }}
            onClick={onToggleOffPlan}
            title="Sheet border, gaps between plans, title block — paper, not building"
          >
            {showOffPlan ? `Hide ${offPlan} off-plan` : `Show ${offPlan} off-plan`}
          </button>
        </div>
      )}

      <div style={S.foot}>
        {panels && panels.length > 1 &&
          `${panels.length} floor plans found on this sheet — each is sectioned separately. `}
        {editable && 'Edit a name or a dimension and the rectangle follows. '}
        {mpp
          ? 'Dimensions are each room’s extent; area is the true shape, so an edited L-shape reads less than its width × depth.'
          : 'Pixels — no scale is set yet, so there is nothing to convert to metres or feet.'}
      </div>
    </div>
  );
}

const S = {
  wrap: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: 10 },
  head: { fontSize: 11.5, fontWeight: 600, color: T.text, marginBottom: 7,
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  cta: { fontSize: 10, fontWeight: 600, padding: '3px 9px', borderRadius: 12,
         cursor: 'pointer', border: `1px solid ${T.border}`,
         background: 'var(--btn-face,#313d4b)', color: 'var(--btn-ink,#e8eef7)' },
  model: { fontSize: 10.5, fontWeight: 600, padding: '4px 14px', borderRadius: 7,
           cursor: 'pointer', border: `1px solid ${T.accent}`,
           background: T.accent, color: '#fff', marginLeft: 'auto' },
  // Blue is the resting state and green means active, matching the analysis
  // buttons elsewhere in the app — the convention has to hold or green stops
  // meaning anything.
  actionOn: { background: 'var(--ok)', border: `1px solid ${'var(--ok)'}` },
  // Save carries the same green the app uses for a committing action (Build 3D,
  // Design Analysis). Reset is deliberately NOT red: it undoes work the user did
  // rather than deleting anything of theirs, and a red button beside a green one
  // reads as danger and gets avoided even when it is the right choice.
  save: { background: 'var(--ok)', border: '1px solid var(--ok)' },
  reset: { marginLeft: 0, background: 'var(--btn-face,#313d4b)',
           border: `1px solid ${T.border}`, color: 'var(--btn-ink,#e8eef7)' },
  footRow: { display: 'flex', justifyContent: 'flex-end', marginTop: 8 },
  ctaOn: { border: `1px solid ${T.accent}`, background: 'color-mix(in srgb, var(--accent) 16%, transparent)', color: T.text },
  dim: { fontSize: 10, color: T.dim, fontWeight: 400 },
  scroll: { maxHeight: 260, overflowY: 'auto', overflowX: 'auto' },
  /* 320px: the width of a 340px compare column once the card's own padding is
     taken off. Any higher and the table would open a horizontal scrollbar
     inside a column it was supposed to fit. Below this the wrapper scrolls
     sideways, which is honest; squeezing on would wrap every figure. */
  table: { width: '100%', minWidth: 320, borderCollapse: 'collapse', fontSize: 11,
           tableLayout: 'fixed' },
  th: { textAlign: 'left', fontSize: 9, whiteSpace: 'nowrap', letterSpacing: 0.4, textTransform: 'uppercase',
        color: T.dim, fontWeight: 600, padding: '4px 6px',
        borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0,
        background: T.surface },
  thUnit: { fontSize: 8.5, fontWeight: 400, letterSpacing: 0.2,
            textTransform: 'none', color: T.dim, marginTop: 1 },
  /* A near-white hairline is invisible on a white table, so in light mode the
     rows ran together. --border-subtle is defined per theme and shows in both. */
  tr: { borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.045))' },
  trOn: { background: 'color-mix(in srgb, var(--accent) 14%, transparent)' },
  td: { padding: '6px 5px', color: T.dim, fontVariantNumeric: 'tabular-nums',
        /* TOP, not middle. Width, Depth and Area each render two lines — the
           value and its imperial equivalent — while Space renders one. Centring
           that single line against a two-line row dropped the name field half a
           line below the number fields beside it. Top-aligned, every first line
           shares a baseline and the imperial lines hang underneath. */
        verticalAlign: 'top' },
  right: { textAlign: 'right' },
  group: { padding: '9px 6px 4px', fontSize: 10, fontWeight: 700,
           letterSpacing: 0.5, textTransform: 'uppercase', color: T.text,
           borderBottom: `1px solid ${T.accent}` },
  groupCount: { marginLeft: 8, fontSize: 9, fontWeight: 500, letterSpacing: 0.3,
                color: T.dim, textTransform: 'none' },
  groupNote: { marginLeft: 8, fontSize: 9, fontWeight: 400, letterSpacing: 0,
               color: T.dim, textTransform: 'none' },
  guess: { marginLeft: 6, fontSize: 8.5, fontWeight: 600, letterSpacing: 0.3,
           padding: '1px 6px', borderRadius: 8, textTransform: 'none',
           background: 'rgba(224,172,74,0.16)', color: 'var(--warn)' },
  /* The same field tokens the Site Parameters inputs use, so an editable cell
     looks like every other place in the app you can type. It was a flat 28%
     BLACK wash, which on the light theme's white table put a dark grey slab in
     every editable cell — heavy to look at and nothing like a text field. */
  /* The same field tokens the Site Parameters inputs use, so an editable cell
     looks like every other place in the app you can type. It was a flat 28%
     BLACK wash, which on the light theme's white table put a dark grey slab in
     every editable cell — heavy to look at and nothing like a text field.

     box-sizing is the important one: there is no global border-box rule in this
     app, so `width: 100%` plus 6px padding and a 1px border made the name field
     14px WIDER than its column and it ran into the Width column beside it. */
  input: { width: '100%', minWidth: 0, boxSizing: 'border-box',
           fontSize: 11, padding: '3px 6px', borderRadius: 6,
           border: '1px solid var(--field-border, #818d9c)',
           background: 'var(--field, #fff)', color: 'var(--field-ink, #111827)',
           outline: 'none', textAlign: 'right',
           fontVariantNumeric: 'tabular-nums',
           transition: 'border-color .12s, box-shadow .12s' },
  inputOn: { borderColor: 'var(--accent, #3b82f6)',
             boxShadow: '0 0 0 2px color-mix(in srgb, var(--accent) 22%, transparent)' },
  primary: { color: T.text, fontVariantNumeric: 'tabular-nums' },
  secondary: { color: T.dim, fontSize: 9.5, marginTop: 1, fontVariantNumeric: 'tabular-nums' },
  badge: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
           width: 17, height: 17, borderRadius: '50%', color: '#0d1420',
           fontSize: 9.5, fontWeight: 700, marginTop: 2 },
  foot: { fontSize: 9.5, color: T.dim, marginTop: 7, lineHeight: 1.45 },
};
